// The single definition of the player-stat rules (Cole's decisions, 2026-09-29;
// app/LagKing/STATS_REDESIGN_2026-09-29.md). Strokes Gained, Session Explorer,
// Sim 9 round stats and Your Game all read their eligibility, make rule, line
// threshold, signs and distance bands from here, so two screens can never
// disagree about the same putt again.
//
// Pure: no React Native imports, so Node tests exercise exactly this code.
//
//   Eligible   finished session (ended_at set), not simulated, has a target.
//   Made       the stored leave is 0, as judged when the putt was hit. There
//              is no re-judge at stimp 10. Only a legacy row with no stored
//              leave falls back to the physics judge, at the session's stimp.
//   On line    |start angle| <= 1.0 degree, high-confidence readings only.
//   Pace miss  distance - target: + is LONG, - is SHORT.
//   Lateral    + is RIGHT, - is LEFT (firmware angle sign).
//   Bands      under 10 ft (start line only, no distance-control metrics),
//              10-20, 20-30, 30-45, 45+ for strokes gained and summaries.
//              Session Explorer adds 5 ft detail buckets from 15 to 60 ft.
//   SG         utils/strokesGained.ts owns the one baseline table.

import { puttSg } from '../utils/strokesGained';
import {
  DEFAULT_STIMP_FT,
  resolveLateralAtCupFt,
  resolveTargetPuttResult,
} from '../utils/targetPutt';

// ---------------------------------------------------------------------------
// Thresholds
// ---------------------------------------------------------------------------

/** Start line counts as "on line" at or inside this many degrees. */
export const ON_LINE_MAX_DEG = 1.0;

/** Below this target, only start line matters: no distance-control metrics. */
export const DISTANCE_CONTROL_MIN_FT = 10;

/** Modes that are practice rather than competition. Everything else counts
 *  as competitive play. Practice still feeds strengths and weaknesses. */
export const PRACTICE_MODES: ReadonlySet<string> = new Set(['target', 'startline']);

export function isPracticeMode(mode: string): boolean {
  return PRACTICE_MODES.has(mode);
}

// ---------------------------------------------------------------------------
// Input row
// ---------------------------------------------------------------------------

/**
 * One putt from the on-device ledger. The Session Explorer / SG query rows
 * satisfy this shape directly. Optional per-putt columns (pace, lateral,
 * judged stimp) are preferred when present and derived when null, so these
 * rules work before and after the ledger migration lands.
 */
export interface LedgerPutt {
  puttId: string;
  sessionId: string;
  mode: string;
  /** ISO; null means the session was never finished. */
  endedAt: string | null;
  /** ISO capture time. */
  createdAt: string;
  targetFt: number | null;
  distanceFt: number;
  /** Stored scoring leave. Null on some legacy rows. */
  proximityFt: number | null;
  /** Null unless the capture advertised an angle solve. */
  angleDeg: number | null;
  angleHighConfidence: boolean;
  /** Session-level stimp the putt was judged at (legacy fallback only). */
  judgingStimpFt?: number | null;
  simulated?: boolean;
  /** Stored signed pace miss (+ long) when the ledger has it. */
  paceMissFt?: number | null;
  /** Stored signed lateral miss (+ right) when the ledger has it. */
  lateralMissFt?: number | null;
  /** Stored per-putt judged stimp when the ledger has it. */
  judgedStimpFt?: number | null;
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Finished, real, aimed putt with a usable measured distance. */
export function isEligiblePutt(putt: LedgerPutt): boolean {
  return (
    putt.endedAt !== null &&
    putt.endedAt !== undefined &&
    putt.endedAt !== '' &&
    putt.simulated !== true &&
    finite(putt.targetFt) &&
    putt.targetFt > 0 &&
    finite(putt.distanceFt) &&
    putt.distanceFt >= 0
  );
}

// ---------------------------------------------------------------------------
// Signs and line
// ---------------------------------------------------------------------------

/** Signed pace miss: + is long, - is short. */
export function paceMissFt(distanceFt: number, targetFt: number): number {
  return distanceFt - targetFt;
}

export type StartLineSide = 'on' | 'left' | 'right';

/** True, false, or null when there is no high-confidence reading. */
export function isOnLine(angleDeg: number | null, highConfidence: boolean): boolean | null {
  if (!highConfidence || angleDeg === null || !finite(angleDeg)) return null;
  return Math.abs(angleDeg) <= ON_LINE_MAX_DEG;
}

/** 'on' inside the threshold, otherwise the side the ball started on. */
export function startLineSide(
  angleDeg: number | null,
  highConfidence: boolean
): StartLineSide | null {
  const onLine = isOnLine(angleDeg, highConfidence);
  if (onLine === null) return null;
  if (onLine) return 'on';
  return (angleDeg as number) < 0 ? 'left' : 'right';
}

// ---------------------------------------------------------------------------
// Distance bands
// ---------------------------------------------------------------------------

export type DistanceBandKey = 'under10' | '10-20' | '20-30' | '30-45' | '45+';

export interface DistanceBand {
  key: DistanceBandKey;
  /** Short label: "10 to 20 ft". */
  label: string;
  /** Sentence fragment: "from 10 to 20 ft". */
  phrase: string;
  /** Inclusive lower edge. */
  minFt: number;
  /** Exclusive upper edge; Infinity for the last band. */
  maxFt: number;
  /** False under 10 ft: start line is the signal there, not pace. */
  distanceControl: boolean;
  /** Target Mode fixed distance that trains this band; null means Start Line. */
  practiceFt: number | null;
}

export const DISTANCE_BANDS: readonly DistanceBand[] = [
  { key: 'under10', label: 'Under 10 ft', phrase: 'under 10 ft', minFt: 0, maxFt: 10, distanceControl: false, practiceFt: null },
  { key: '10-20', label: '10 to 20 ft', phrase: 'from 10 to 20 ft', minFt: 10, maxFt: 20, distanceControl: true, practiceFt: 15 },
  { key: '20-30', label: '20 to 30 ft', phrase: 'from 20 to 30 ft', minFt: 20, maxFt: 30, distanceControl: true, practiceFt: 25 },
  { key: '30-45', label: '30 to 45 ft', phrase: 'from 30 to 45 ft', minFt: 30, maxFt: 45, distanceControl: true, practiceFt: 35 },
  { key: '45+', label: '45 ft plus', phrase: 'from 45 ft and beyond', minFt: 45, maxFt: Infinity, distanceControl: true, practiceFt: 50 },
];

export function bandFor(targetFt: number): DistanceBand | null {
  if (!finite(targetFt) || targetFt <= 0) return null;
  return DISTANCE_BANDS.find((band) => targetFt >= band.minFt && targetFt < band.maxFt) ?? null;
}

export function bandByKey(key: DistanceBandKey): DistanceBand {
  return DISTANCE_BANDS.find((band) => band.key === key) as DistanceBand;
}

// ---------------------------------------------------------------------------
// Session Explorer 5 ft detail buckets
// ---------------------------------------------------------------------------

export const DETAIL_MIN_FT = 15;
export const DETAIL_MAX_FT = 60;
export const DETAIL_STEP_FT = 5;
export const DETAIL_BUCKETS_FT = [15, 20, 25, 30, 35, 40, 45, 50, 55, 60] as const;

export interface DetailBucketDef {
  bucketFt: number;
  /** Compact selector label. */
  label: string;
  /** Inclusive lower edge of the targets this bucket holds. */
  minTargetFt: number;
  /** Upper edge (exclusive, except the last bucket, which includes 60). */
  maxTargetFt: number;
  /** "27.5 to 32.5 ft": computed from the same cut points as bucketing. */
  rangeLabel: string;
}

function formatEdge(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/**
 * Nearest five feet, clamped to 15-60 ft. Every target from 15 through 60 ft
 * lands in exactly one bucket; nothing in range is dropped.
 */
export function detailBucketFor(targetFt: number): number | null {
  if (!finite(targetFt) || targetFt < DETAIL_MIN_FT || targetFt > DETAIL_MAX_FT) return null;
  const nearest = Math.round(targetFt / DETAIL_STEP_FT) * DETAIL_STEP_FT;
  return Math.max(DETAIL_MIN_FT, Math.min(DETAIL_MAX_FT, nearest));
}

/**
 * Labels are derived from the rounding cut points (bucket +/- 2.5 ft), so a
 * 17.3 ft target sits in "15 to 17.5 ft" and 17.6 ft in "17.5 to 22.5 ft".
 */
export const DETAIL_BUCKET_DEFS: readonly DetailBucketDef[] = DETAIL_BUCKETS_FT.map(
  (bucketFt, index) => {
    const minTargetFt = index === 0 ? DETAIL_MIN_FT : bucketFt - DETAIL_STEP_FT / 2;
    const maxTargetFt =
      index === DETAIL_BUCKETS_FT.length - 1 ? DETAIL_MAX_FT : bucketFt + DETAIL_STEP_FT / 2;
    return {
      bucketFt,
      label: `${bucketFt} ft`,
      minTargetFt,
      maxTargetFt,
      rangeLabel: `${formatEdge(minTargetFt)} to ${formatEdge(maxTargetFt)} ft`,
    };
  }
);

// ---------------------------------------------------------------------------
// Judging one putt
// ---------------------------------------------------------------------------

export interface JudgedPutt {
  puttId: string;
  sessionId: string;
  mode: string;
  createdAt: string;
  /** Parsed createdAt; NaN when unparseable. */
  createdMs: number;
  targetFt: number;
  distanceFt: number;
  band: DistanceBandKey;
  practice: boolean;
  isMake: boolean;
  /** Leave used for proximity and SG (0 when made). */
  proximityFt: number;
  /** + long, - short. */
  paceMissFt: number;
  /** + right, - left; null without an angle. */
  lateralMissFt: number | null;
  angleDeg: number | null;
  angleHighConfidence: boolean;
  onLine: boolean | null;
  side: StartLineSide | null;
  sg: number;
}

/**
 * Judge one eligible putt under the shared rules. Returns null for a putt
 * that fails eligibility, so callers can map-and-filter.
 */
export function judgePutt(putt: LedgerPutt): JudgedPutt | null {
  if (!isEligiblePutt(putt)) return null;
  const targetFt = putt.targetFt as number;
  const band = bandFor(targetFt);
  if (!band) return null;

  const angleDeg = finite(putt.angleDeg) ? putt.angleDeg : null;
  const highConfidence = angleDeg !== null && putt.angleHighConfidence === true;
  const storedLeave = finite(putt.proximityFt) ? Math.abs(putt.proximityFt) : null;

  let isMake: boolean;
  let proximityFt: number;
  if (storedLeave !== null) {
    // The verdict stored at capture wins. Another phone can change the gate's
    // stimp mid-session, and the exact judging stimp is not always persisted,
    // so a re-judge here could flip a make the player saw into a miss.
    isMake = storedLeave === 0;
    proximityFt = storedLeave;
  } else {
    // Legacy row with no stored leave: best-available reconstruction at the
    // stimp the putt (or its session) was judged at.
    const stimp = finite(putt.judgedStimpFt) && putt.judgedStimpFt > 0
      ? putt.judgedStimpFt
      : finite(putt.judgingStimpFt) && putt.judgingStimpFt > 0
        ? putt.judgingStimpFt
        : DEFAULT_STIMP_FT;
    const result = resolveTargetPuttResult({
      angleDeg,
      distanceFt: putt.distanceFt,
      fallbackProximityFt: null,
      targetFt,
      stimpFt: stimp,
    });
    isMake = result.isMake;
    proximityFt = isMake ? 0 : result.proximityFt ?? Math.abs(putt.distanceFt - targetFt);
  }

  const pace = finite(putt.paceMissFt) ? putt.paceMissFt : paceMissFt(putt.distanceFt, targetFt);
  const lateral = finite(putt.lateralMissFt)
    ? putt.lateralMissFt
    : angleDeg === null
      ? null
      : resolveLateralAtCupFt(angleDeg, targetFt);
  const createdMs = Date.parse(putt.createdAt);

  return {
    puttId: putt.puttId,
    sessionId: putt.sessionId,
    mode: putt.mode,
    createdAt: putt.createdAt,
    createdMs,
    targetFt,
    distanceFt: putt.distanceFt,
    band: band.key,
    practice: isPracticeMode(putt.mode),
    isMake,
    proximityFt,
    paceMissFt: pace,
    lateralMissFt: lateral,
    angleDeg,
    angleHighConfidence: highConfidence,
    onLine: isOnLine(angleDeg, highConfidence),
    side: startLineSide(angleDeg, highConfidence),
    sg: puttSg({ targetFt, proximityFt, holed: isMake, distanceMissFt: pace }),
  };
}

export function judgeLedger(putts: readonly LedgerPutt[]): JudgedPutt[] {
  const judged: JudgedPutt[] = [];
  for (const putt of putts) {
    const result = judgePutt(putt);
    if (result) judged.push(result);
  }
  return judged;
}

// ---------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------

/**
 * Non-destructive SG reset: an absent or invalid marker keeps all history; a
 * valid marker keeps putts captured at or after it.
 */
export function inSgResetWindow(createdAt: string, resetAtIso: string | null | undefined): boolean {
  const resetMs = resetAtIso ? Date.parse(resetAtIso) : NaN;
  if (!Number.isFinite(resetMs) || resetMs <= 0) return true;
  return Date.parse(createdAt) >= resetMs;
}

// ---------------------------------------------------------------------------
// Pace and line tallies (shared by every summary)
// ---------------------------------------------------------------------------

export interface PaceTally {
  /** Non-made putts with a nonzero pace miss. */
  misses: number;
  short: number;
  long: number;
  /** Mean signed pace miss over non-made putts (+ long). */
  meanPaceMissFt: number | null;
  shortRate: number | null;
  longRate: number | null;
}

/** Makes are neither short nor long. A dead-weight miss counts as neither. */
export function tallyPace(putts: readonly Pick<JudgedPutt, 'isMake' | 'paceMissFt'>[]): PaceTally {
  let short = 0;
  let long = 0;
  let sum = 0;
  let n = 0;
  for (const putt of putts) {
    if (putt.isMake || !finite(putt.paceMissFt)) continue;
    sum += putt.paceMissFt;
    n += 1;
    if (putt.paceMissFt < 0) short += 1;
    else if (putt.paceMissFt > 0) long += 1;
  }
  const misses = short + long;
  return {
    misses,
    short,
    long,
    meanPaceMissFt: n > 0 ? sum / n : null,
    shortRate: misses > 0 ? short / misses : null,
    longRate: misses > 0 ? long / misses : null,
  };
}

export interface LineTally {
  /** High-confidence readings. */
  readings: number;
  onLine: number;
  left: number;
  right: number;
  onLineRate: number | null;
}

export function tallyLine(putts: readonly Pick<JudgedPutt, 'side'>[]): LineTally {
  let on = 0;
  let left = 0;
  let right = 0;
  for (const putt of putts) {
    if (putt.side === 'on') on += 1;
    else if (putt.side === 'left') left += 1;
    else if (putt.side === 'right') right += 1;
  }
  const readings = on + left + right;
  return { readings, onLine: on, left, right, onLineRate: readings > 0 ? on / readings : null };
}

export type LineLean = 'left' | 'right' | 'balanced' | null;

/** Lean from off-line readings only; on-line putts never vote. */
export function leanOf(tally: Pick<LineTally, 'left' | 'right'>): LineLean {
  if (tally.left + tally.right === 0) return null;
  if (tally.left === tally.right) return 'balanced';
  return tally.left > tally.right ? 'left' : 'right';
}
